import { faCommentSlash } from "@fortawesome/pro-regular-svg-icons/faCommentSlash";
import { faEyeSlash } from "@fortawesome/pro-regular-svg-icons/faEyeSlash";
import { faCommentSms } from "@fortawesome/pro-solid-svg-icons/faCommentSms";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { MprcFormSubHeader, MprcIconButton, MprcIconFa, MprcStringToHtml } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcButton } from "components/MprcButton";
import { MprcContainer } from "components/MprcContainer";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcPhoneField } from "components/MprcPhoneField";
import { CustomFieldValueDto, ManagedUserQueryResponse, UserPhoneDto, UserPhoneType, UserSocialNetworkDto, UserSocialNetworkType } from "generated/CoreApi_v1";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput, getCustomFieldGridSize } from "views/foundation/customField/components/CustomFieldInput";
import { getSocialNetworkIcon, getSocialNetworkUrl } from "views/foundation/user/Home/EditProfile/EditUserPersonInfoBox";
import { useEditUserContext } from "views/foundation/user/Home/EditUser/EditUser";
import { EditUserIdiomProficiency } from "./EditUserIdiomProficiency";

////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    birthDate: boolean;
    customFields: { [key: string]: boolean; };
    phones: { [index: number]: boolean; };
}

export interface IPostValidation {
    birthDate: string[] | undefined;
    customFields: { [key: string]: string[] | undefined; };
    phones: { [index: number]: string[] | undefined; };
}

export interface IUserPersonalInfoBoxData {
    userId: string;
    phones: UserPhoneDto[];
    customFieldsPersonal: CustomFieldValueDto[] | null;
    socialNetworks: UserSocialNetworkDto[];
    birthDate: Date | null;
}

interface IProps {
    viewData: IUserPersonalInfoBoxData;
    onFormValidation: (valid: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////

///
function getDefaultValidate(viewData: IUserPersonalInfoBoxData): IValidationFields {
    var obj = { customFields: {}, birthDate: true, phones: {} } as IValidationFields;
    viewData.customFieldsPersonal
        ?.filter((x) => x.isRequired)
        .forEach((f) => (obj.customFields[f.fieldId] = f.fieldValue != undefined && f.fieldValue.toString().length > 0));

    viewData.phones?.forEach((p, i) => {
        obj.phones[i] = true;
    });
    return obj;
}

/**
 * Edição de Usuário - Informações Pessoais
 * @param props
 */
export const EditUserPersonInfoBox: React.FC<IProps> = (props) => {
    const { t } = useTranslation();

    const { viewData, onFormValidation } = props;

    const { viewErrors, isEditMode, isNewUser, readonly, phoneTypeItems, onGetUsername, onDialog, onFormUpdate, onGoToSubForm } = useEditUserContext();

    const [formValidation, setFormValidation] = useState<IValidationFields>(getDefaultValidate(viewData));
    const [postValidation, setPostValidation] = useState<IPostValidation>({ customFields: {}, phones: {} } as IPostValidation);

    ////////////////////////////////////////////////////////////////////////////
    ///
    const handleUpdateRecord = (name: keyof ManagedUserQueryResponse, v: any) => {
        onFormUpdate({ [name]: v });
    };

    ///
    const handleAddPhone = () => {
        const defaultPhone = {
            phoneNumber: "",
            phoneType: UserPhoneType.Work,
            showOnProfile: false,
            usedForSMS: null
        } as UserPhoneDto;
        handleUpdateRecord("phones", viewData.phones ? [...viewData.phones, defaultPhone] : [defaultPhone]);
    };

    ///
    const handleUpdatePhone = (index: number, prop: keyof UserPhoneDto, v: any, isValid?: boolean) => {
        if (!viewData.phones) return;

        const checkUsedForSMS = prop === "usedForSMS" && v === true;

        const phones = viewData.phones.map((item, i) => {
            if (i === index) {
                item = { ...item, [prop]: v };
            } else {
                if (checkUsedForSMS && item.usedForSMS === true) item.usedForSMS = false;
            }
            return item;
        });

        if (isValid != null) {
            setFormValidation((prev) => ({
                ...prev,
                phones: { ...prev.phones, [index]: isValid }
            }));

            setPostValidation((prev) => ({
                ...prev,
                phones: { ...prev.phones, [index]: isValid ? undefined : prev.phones[index] }
            }));
        }

        handleUpdateRecord("phones", phones);
    };

    ///
    const handleDeletePhone = (index: number) => {
        if (!viewData.phones) return;

        const phones = viewData.phones.filter((item, i) => i != index);
        setFormValidation((prev) => {
            const newPhones = { ...prev.phones };
            delete newPhones[index];
            return { ...prev, phones: newPhones };
        });
        handleUpdateRecord("phones", phones);
    };

    ///
    const handleChangeSocialNetwork = (socialNetworkType: UserSocialNetworkType, value: string) => {
        const socialNetworks = structuredClone(viewData.socialNetworks);
        socialNetworks.map((item) => {
            if (item.socialNetworkType === socialNetworkType) {
                item.socialNetworkId = value;
            }

            return item;
        });

        handleUpdateRecord("socialNetworks", socialNetworks);
    };

    ///

    const handleUpdateCustomField = (field: CustomFieldValueDto, value: any, isValid: boolean) => {
        let newCustomFields = viewData.customFieldsPersonal?.map((item) => {
            if (item.fieldId === field.fieldId) item.fieldValue = value;
            return item;
        });

        handleUpdateRecord("customFieldsPersonal", newCustomFields);

        if (field.fieldValue != undefined || formValidation.customFields[field.fieldId] === false)
            setFormValidation({
                ...formValidation,
                customFields: { ...formValidation.customFields, [field.fieldId]: isValid }
            });
    };

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        const isValid = formValidation.birthDate;
        const phonesValid = Object.values(formValidation.phones).filter((x) => x === false).length === 0;
        if (Object.keys(formValidation.customFields).length === 0) {
            onFormValidation(isValid && phonesValid); //Se a tab não tiver campos custom obrigatórios, a validação retorna True
        } else {
            onFormValidation(Object.values(formValidation.customFields).filter((x) => x === false).length === 0 && phonesValid && isValid);
        }
    }, [formValidation]);

    useEffect(() => {
        if (viewErrors != null) {

            const postValidationFields: { [key: string]: string[]; } = {};
            const postValidationPhones: { [key: string]: string[]; } = {};
            setFormValidation(() => {

                var customFields = viewData.customFieldsPersonal?.reduce(
                    (accumulator, currValue) => {
                        if (viewErrors.hasOwnProperty(`customFields.${currValue.fieldId}`)) {
                            accumulator[currValue.fieldId] = false;
                            postValidationFields[currValue.fieldId] = viewErrors[`customFields.${currValue.fieldId}`];
                        }
                        return accumulator;
                    }, {} as { [key: string]: boolean; }) ?? {};

                var phones = viewData.phones?.reduce(
                    (accumulator, _, index) => {
                        if (viewErrors.hasOwnProperty(`phones[${index}]`)) {
                            accumulator[index] = false;
                            postValidationPhones[index] = viewErrors[`phones[${index}]`];
                        }
                        return accumulator;
                    }, {} as { [key: string]: boolean; }) ?? {};

                return { customFields: customFields, birthDate: viewErrors["birthDate"] === undefined, phones: phones };
            }
            );

            setPostValidation(({ customFields: postValidationFields, birthDate: viewErrors["birthDate"], phones: postValidationPhones }));
        }
    }, [viewErrors]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcContainer margin="large">
                <MprcFormSubHeader
                    title={t("User.Edit.PhonesSubtitle")}
                />
                <Grid container rowSpacing={2}>
                    {viewData.phones?.map((phone, i) => (
                        <Grid size={{ xs: 12 }} key={i}>
                            <Grid container justifyContent="space-between">
                                <Grid size={{ xs: 8 }} container columnSpacing={4} alignItems="center" alignContent="center">
                                    <Grid size={{ xs: 6 }}>
                                        <FormControl fullWidth margin="dense">
                                            <InputLabel>{t("User.Edit.PhoneTypePlaceholder")}</InputLabel>
                                            <Select
                                                displayEmpty
                                                disabled={readonly}
                                                value={phoneTypeItems.length > 0 ? phone.phoneType : ""}
                                                onChange={(e) => handleUpdatePhone(i, "phoneType", e.target.value, true)}
                                            >
                                                {phoneTypeItems.map((item, i) => (
                                                    <MenuItem key={i} value={item.type}>
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <MprcPhoneField
                                            autoFocus
                                            fullWidth
                                            disabled={readonly}
                                            value={phone.phoneNumber ?? ""}
                                            onChange={(value, _, isValid) => handleUpdatePhone(i, "phoneNumber", value, isValid)}
                                            label={t("User.Edit.PhoneLabel")}
                                            onClickDelete={() => handleDeletePhone(i)}
                                            error={i in formValidation.phones && formValidation.phones[i] === false}
                                            helperText={
                                                i in (postValidation?.phones ?? []) && (
                                                    <MprcStringToHtml text={postValidation?.phones[i]} color="error" />
                                                )
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                <Grid size="grow" container justifyContent="end" alignItems="end" columnSpacing={1}>
                                    <Grid>
                                        <MprcIconButton
                                            size="medium"
                                            disabled={readonly}
                                            color={phone.showOnProfile ? "highlight" : "inherit"}
                                            onClick={() => handleUpdatePhone(i, "showOnProfile", !phone.showOnProfile)}
                                            title={t("User.Edit.ShowOnProfileButtonTooltip")}
                                        >
                                            <MprcIconFa
                                                icon={phone.showOnProfile === true ? faEye : faEyeSlash}
                                                fixedWidth
                                            />
                                        </MprcIconButton>
                                    </Grid>
                                    <Grid>
                                        <MprcIconButton
                                            size="medium"
                                            disabled={readonly}
                                            color={phone.usedForSMS ? "highlight" : "inherit"}
                                            onClick={() => handleUpdatePhone(i, "usedForSMS", !phone.usedForSMS)}
                                            title={t("User.Edit.SMSButtonTooltip")}
                                        >
                                            <MprcIconFa
                                                icon={phone.usedForSMS === true ? faCommentSms : faCommentSlash}
                                                fixedWidth
                                            />
                                        </MprcIconButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}
                    <Grid size={{ xs: 12 }}>
                        <ButtonAdd
                            size="large"
                            disabled={readonly}
                            text={viewData.phones.length > 0 ? t("User.Edit.AddPhoneButtonText_other") : t("User.Edit.AddPhoneButtonText_one")}
                            color="info"
                            variant="text"
                            onClick={handleAddPhone}
                        />
                    </Grid>
                </Grid>

                <MprcDatePicker
                    margin="dense"
                    label={t("User.Edit.BirthDateLabel")}
                    width={220}
                    value={viewData.birthDate}
                    maxDate={new Date()}
                    disabled={readonly}
                    onChange={(date, valid) => {
                        handleUpdateRecord("birthDate", date);
                        setFormValidation({
                            ...formValidation,
                            birthDate: date == null || (moment(date).toDate() > new Date(1900, 1, 1) && moment(date).toDate() < new Date())
                        });
                    }}
                    helperText={
                        postValidation?.birthDate && (
                            <MprcStringToHtml text={postValidation?.birthDate} color="error" />
                        )
                    }
                />
            </MprcContainer>

            <MprcContainer margin="large">
                <MprcFormSubHeader
                    margin="dense"
                    title={t("User.Edit.SocialNetworksSubtitle")}
                />
                <Grid container rowSpacing={2}>
                    {viewData.socialNetworks?.map((item) => (
                        <Grid size={{ xs: 12 }} key={item.socialNetworkType}>
                            <TextField
                                fullWidth
                                disabled={readonly}
                                value={item.socialNetworkId}
                                placeholder={item.socialNetworkTypeName}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start"
                                                sx={{
                                                    minWidth: "220px",
                                                    padding: "8px",
                                                    backgroundColor: (theme) => theme.palette.divider,
                                                    borderTopLeftRadius: (theme) => theme.shape.borderRadius + "px",
                                                    borderBottomLeftRadius: (theme) => theme.shape.borderRadius + "px"
                                                }}
                                            >
                                                <MprcIconFa icon={getSocialNetworkIcon(item.socialNetworkType)} size="lg" color="muted" fixedWidth />
                                                &nbsp;
                                                {getSocialNetworkUrl(item.socialNetworkType)}
                                                <span>/</span>
                                            </InputAdornment>
                                        )
                                    }
                                }}
                                onChange={(e) => handleChangeSocialNetwork(item.socialNetworkType, e.target.value)}
                            />
                        </Grid>
                    ))}
                </Grid>
            </MprcContainer>

            {viewData.customFieldsPersonal && viewData.customFieldsPersonal.length > 0 && (
                <MprcContainer margin="large">
                    <MprcFormSubHeader
                        margin="dense"
                        title={t("User.Edit.OtherInfoSubtitle")}
                    />
                    <Grid container rowSpacing={1} columnSpacing={4}>
                        {viewData.customFieldsPersonal.map((item, i) => (
                            <Grid key={i} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                <CustomFieldInput
                                    field={item}
                                    disabled={readonly}
                                    useDefaultValue={isNewUser}
                                    onChange={(value, formattedValue, isValid) => handleUpdateCustomField(item, value, isValid)}
                                    error={postValidation.customFields[item.fieldId]}
                                    validateOnFirstMount={true}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </MprcContainer>
            )}

            <MprcContainer margin="large">
                <MprcFormSubHeader
                    margin="dense"
                    title={t("User.Edit.ResumeAndProfileSubtitle")}
                    subtitle={t("User.Edit.EditSubFormHint")}
                />
                <Grid container spacing={1}>
                    <Grid>
                        <MprcButton
                            title={t("User.Edit.InterestTopicsButtonTooltip")}
                            text={t("User.Edit.InterestTopicsButtonText")}
                            variant="contained"
                            rounded
                            disabled={isEditMode}
                            onClick={() => onGoToSubForm("topics")}
                        />
                    </Grid>
                    <Grid>
                        <MprcButton
                            title={t("User.Edit.CredentialsAndExperiencesButtonTooltip")}
                            text={t("User.Edit.CredentialsAndExperiencesButtonText")}
                            variant="contained"
                            rounded
                            disabled={isEditMode}
                            onClick={() => onGoToSubForm("credentials")}
                        />
                    </Grid>
                    <Grid>
                        <MprcButton
                            title={t("User.Edit.LanguageProficiencyButtonTooltip")}
                            text={t("User.Edit.LanguageProficiencyButtonText")}
                            variant="contained"
                            rounded
                            disabled={isEditMode}
                            onClick={() =>
                                onDialog(
                                    <EditUserIdiomProficiency
                                        userId={viewData.userId}
                                        userName={onGetUsername()}
                                        readonly={readonly}
                                        onClose={() => onDialog(null)}
                                    />
                                )
                            }
                        />
                    </Grid>
                </Grid>
            </MprcContainer>
        </>
    );
};
